import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setUser, setAuthToken, setAuthType } from '../../store/slices/userSlice';
import routes from '../../utils/routes';
import strings from '../../utils/strings';
import { CButton, CInput, CText, CHeader } from '../../components/common';
import { requestOTP, handlePostLoginData, fetchUserData } from '../../api/auth';
import { setAuthToken as saveAuthToken, setAuthType as saveAuthType, setUserData as saveUserData } from '../../utils/localStorage';
import { GOOGLE_AUTH_TYPE } from '../../utils/constants';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const GOOGLE_AUTH_ENABLED = !!GOOGLE_CLIENT_ID;

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const colors = useAppSelector((state) => state.theme.theme);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberError, setPhoneNumberError] = useState('');
  const [countryCode, setCountryCode] = useState('+91');
  const [loading, setLoading] = useState(false);

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true);
    try {
      const idToken = credentialResponse.credential;
      const response = await handlePostLoginData(idToken, 'GOOGLE');

      saveAuthToken(response.access || '');
      saveAuthType(GOOGLE_AUTH_TYPE);
      dispatch(setAuthToken(response.access || ''));
      dispatch(setAuthType(GOOGLE_AUTH_TYPE));

      const userData = await fetchUserData(response.access || '');
      dispatch(setUser(userData));
      saveUserData(userData);

      if (!userData.user_exists) {
        navigate(routes.userDetails);
      } else {
        navigate(routes.home);
      }
    } catch (error: any) {
      console.error('Error with Google login:', error);
      alert(error.response?.data?.error || 'Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    alert('Google login failed. Please try again.');
  };

  const handleGetOTP = async () => {
    let isValid = true;

    if (!phoneNumber) {
      setPhoneNumberError(strings.thisFieldIsMandatory);
      isValid = false;
    } else if (phoneNumber.length < 10) {
      setPhoneNumberError('Please enter a valid phone number');
      isValid = false;
    } else {
      setPhoneNumberError('');
    }

    if (isValid) {
      setLoading(true);
      try {
        const fullPhoneNumber = `${countryCode}${phoneNumber}`.trim();
        await requestOTP(fullPhoneNumber);

        // Navigate to OTP verification page
        navigate(routes.otpVerification || '/otp-verification', {
          state: { phoneNumber: fullPhoneNumber },
        });
      } catch (error: any) {
        console.error('Error requesting OTP:', error);
        setPhoneNumberError(
          error.response?.data?.error ||
          error.response?.data?.message ||
          'Failed to send OTP. Please try again.'
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: colors.backgroundColor }}
    >
      <CHeader title={strings.signIn} />

      {/* Responsive container - centered with max-width */}
      <div className="max-w-md md:max-w-lg lg:max-w-xl mx-auto px-5 py-4">
        <h2
          className="text-2xl md:text-3xl font-bold mb-2"
          style={{ color: colors.textColor }}
        >
          {strings.welcomeBack}
        </h2>
        <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '16px' }}>
          {strings.signInToYourAccount}
        </CText>

        <div className="mb-3">
          <CText type="r14" color={colors.grayScale500} style={{ marginBottom: '4px' }}>
            Country Code
          </CText>
          <select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="w-full p-3 rounded-xl border text-base font-sans"
            style={{
              borderColor: colors.borderColor,
              backgroundColor: colors.inputBackground,
              color: colors.textColor,
            }}
          >
            <option value="+91">🇮🇳 India (+91)</option>
            <option value="+1">🇺🇸 USA (+1)</option>
            <option value="+44">🇬🇧 UK (+44)</option>
            <option value="+971">🇦🇪 UAE (+971)</option>
          </select>
        </div>

        <CInput
          label={strings.phoneNumber}
          value={phoneNumber}
          onChange={setPhoneNumber}
          placeholder="Enter your phone number"
          type="tel"
          maxLength={10}
          errorText={phoneNumberError}
          required
        />

        <CButton
          title={loading ? 'Sending OTP...' : strings.getOTP}
          onPress={handleGetOTP}
          disabled={loading}
        />

        {GOOGLE_AUTH_ENABLED && (
          <>
            <div className="flex items-center my-4 gap-3">
              <div className="flex-1 h-px" style={{ backgroundColor: colors.grayScale400 }} />
              <CText type="r12" color={colors.grayScale500}>OR</CText>
              <div className="flex-1 h-px" style={{ backgroundColor: colors.grayScale400 }} />
            </div>

            <div className="flex justify-center mb-4">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
              />
            </div>
          </>
        )}

        <div className="text-center mt-4">
          <CText type="r14" color={colors.grayScale500}>
            {strings.doHaveAnAccount}
            <span
              className="cursor-pointer ml-1"
              style={{ color: colors.primary }}
              onClick={() => navigate(routes.signUp)}
            >
              {strings.signUp}
            </span>
          </CText>
        </div>
      </div>
    </div>
  );
};

const LoginWithProvider: React.FC = () => {
  if (!GOOGLE_AUTH_ENABLED) {
    return <Login />;
  }
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <Login />
    </GoogleOAuthProvider>
  );
};

export default LoginWithProvider;
